import React, { useState, useEffect } from "react";
import { apiService } from "../../utils/api";
import { FaEdit, FaTrash, FaCheck, FaTimes, FaSearch, FaFilter } from "react-icons/fa";
import "./TeacherManagement.css";

function TeacherManagement() {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTeacher, setNewTeacher] = useState({
    username: "",
    email: "",
    password: "",
  });

  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("table");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchTeachers();
  }, []);

  const fetchTeachers = async () => {
    try {
      setLoading(true);
      const response = await apiService.getTeachers();
      if (response.data.success) {
        setTeachers(response.data.teachers);
      } else {
        setError(response.data.message || "Failed to fetch teachers");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error connecting to server");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewTeacher((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setFormSuccess("");

    if (!isEditing && (!newTeacher.username || !newTeacher.email || !newTeacher.password)) {
      setFormError("All fields are required");
      return;
    }

    if (isEditing && (!newTeacher.username || !newTeacher.email)) {
      setFormError("Username and email are required");
      return;
    }

    try {
      setSubmitting(true);
      let response;

      if (isEditing) {
        response = await apiService.updateTeacher(newTeacher.id, newTeacher);
      } else {
        response = await apiService.addTeacher(newTeacher);
      }

      if (response.data.success) {
        setFormSuccess(isEditing ? "Teacher updated successfully!" : "Teacher added successfully!");
        setNewTeacher({
          username: "",
          email: "",
          password: "",
        });
        setShowAddForm(false);
        setIsEditing(false);
        fetchTeachers();
      } else {
        setFormError(response.data.message || (isEditing ? "Failed to update teacher" : "Failed to add teacher"));
      }
    } catch (err) {
      setFormError(err.response?.data?.message || "Error connecting to server");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const showUpdateForm = (teacher) => {
    setNewTeacher({
      id: teacher.id,
      username: teacher.username,
      email: teacher.email,
      password: "", // Don't include the password when editing
    });
    setIsEditing(true);
    setShowAddForm(true);
  };

  const handleDeleteTeacher = async (teacherId) => {
    if (!window.confirm("Are you sure you want to delete this teacher?")) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.deleteTeacher(teacherId);

      if (response.data.success) {
        setFormSuccess("Teacher deleted successfully!");
        fetchTeachers();
      } else {
        setError(response.data.message || "Failed to delete teacher");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error connecting to server");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const cancelForm = () => {
    setNewTeacher({
      username: "",
      email: "",
      password: "",
    });
    setIsEditing(false);
    setShowAddForm(false);
    setFormError("");
    setFormSuccess("");
  };

  const handleStatusChange = async (teacherId, currentStatus) => {
    const newStatus = currentStatus === "active" ? "blocked" : "active";
    const actionText = newStatus === "active" ? "unblock" : "block";

    if (!window.confirm(`Are you sure you want to ${actionText} this teacher?`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.updateUserStatus(teacherId, newStatus);

      if (response.data.success) {
        setFormSuccess(`Teacher ${newStatus === "active" ? "unblocked" : "blocked"} successfully!`);
        fetchTeachers();
      } else {
        setError(response.data.message || `Failed to ${actionText} teacher`);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error connecting to server");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Filter teachers based on search term
  const filteredTeachers = teachers.filter(teacher => 
    teacher.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    teacher.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="teacher-management">
      <div className="section-header">
        <h2>Teacher Management</h2>
        <button
          className="add-button"
          onClick={() => {
            if (showAddForm) {
              cancelForm();
            } else {
              setShowAddForm(true);
            }
          }}
        >
          {showAddForm ? "Cancel" : "Add Teacher"}
        </button>
      </div>

      {showAddForm && (
        <div className="add-teacher-form">
          <h3>{isEditing ? "Edit Teacher" : "Add New Teacher"}</h3>
          {formError && <p className="error-message">{formError}</p>}
          {formSuccess && <p className="success-message">{formSuccess}</p>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Username:</label>
              <input type="text" name="username" value={newTeacher.username} onChange={handleInputChange} placeholder="Enter username" />
            </div>

            <div className="form-group">
              <label>Email:</label>
              <input type="email" name="email" value={newTeacher.email} onChange={handleInputChange} placeholder="Enter email" />
            </div>

            {!isEditing && (
              <div className="form-group">
                <label>Password:</label>
                <input type="password" name="password" value={newTeacher.password} onChange={handleInputChange} placeholder="Enter password" />
              </div>
            )}

            {isEditing && (
              <div className="form-group">
                <label>New Password (leave blank to keep current):</label>
                <input type="password" name="password" value={newTeacher.password} onChange={handleInputChange} placeholder="Enter new password" />
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="submit-button" disabled={submitting}>
                {submitting ? "Please wait..." : isEditing ? "Update Teacher" : "Add Teacher"}
              </button>

              <button type="button" className="cancel-button" onClick={cancelForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading teachers...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <p className="error-message">{error}</p>
          <button onClick={fetchTeachers} className="retry-button">Try Again</button>
        </div>
      ) : (
        <div className="teachers-list">
          <div className="list-header">
            <h3>Current Teachers</h3>
            
            <div className="list-controls">
              <div className="search-container">
                <FaSearch className="search-icon" />
                <input 
                  type="text" 
                  placeholder="Search teachers..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
              
              <div className="view-toggle">
                <button 
                  className={`view-button ${viewMode === 'table' ? 'active' : ''}`}
                  onClick={() => setViewMode('table')}
                >
                  Table
                </button>
                <button 
                  className={`view-button ${viewMode === 'cards' ? 'active' : ''}`}
                  onClick={() => setViewMode('cards')}
                >
                  Cards
                </button>
              </div>
            </div>
          </div>
          
          {filteredTeachers.length === 0 ? (
            <div className="empty-state">
              <p>No teachers found.</p>
              {searchTerm && <p>Try adjusting your search criteria.</p>}
            </div>
          ) : viewMode === 'table' ? (
            <div className="responsive-table-container">
              <table className="responsive-table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Created At</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTeachers.map((teacher) => (
                    <tr key={teacher.id} className={teacher.status === "blocked" ? "blocked-user" : ""}>
                      <td data-label="Username">{teacher.username}</td>
                      <td data-label="Email">{teacher.email}</td>
                      <td data-label="Created At">{new Date(teacher.createdAt).toLocaleDateString()}</td>
                      <td data-label="Status">
                        <div className="status-switch-container">
                          <label className="switch">
                            <input 
                              type="checkbox" 
                              checked={teacher.status === "active"} 
                              onChange={() => handleStatusChange(teacher.id, teacher.status)}
                            />
                            <span className="slider round"></span>
                          </label>
                          <span className={`status-label ${teacher.status}`}>
                            {teacher.status === "active" ? 
                              <><FaCheck className="status-icon active" /> Active</> : 
                              <><FaTimes className="status-icon blocked" /> Blocked</>
                            }
                          </span>
                        </div>
                      </td>
                      <td data-label="Actions" className="action-buttons">
                        <button onClick={() => showUpdateForm(teacher)} className="action-button edit">
                          <FaEdit />
                          <span className="button-text">Edit</span>
                        </button>
                        <button onClick={() => handleDeleteTeacher(teacher.id)} className="action-button delete">
                          <FaTrash />
                          <span className="button-text">Delete</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="teacher-cards">
              {filteredTeachers.map((teacher) => (
                <div key={teacher.id} className={`teacher-card ${teacher.status === "blocked" ? "blocked-card" : ""}`}>
                  <div className="card-header">
                    <div className="teacher-avatar">
                      {teacher.username.charAt(0).toUpperCase()}
                    </div>
                    <div className="teacher-info">
                      <h4 className="teacher-name">{teacher.username}</h4>
                      <p className="teacher-email">{teacher.email}</p>
                    </div>
                  </div>
                  
                  <div className="card-body">
                    <div className="info-row">
                      <span className="info-label">Created:</span>
                      <span className="info-value">{new Date(teacher.createdAt).toLocaleDateString()}</span>
                    </div>
                    
                    <div className="info-row">
                      <span className="info-label">Status:</span>
                      <div className="status-switch-container">
                        <label className="switch">
                          <input 
                            type="checkbox" 
                            checked={teacher.status === "active"} 
                            onChange={() => handleStatusChange(teacher.id, teacher.status)}
                          />
                          <span className="slider round"></span>
                        </label>
                        <span className={`status-label ${teacher.status}`}>
                          {teacher.status === "active" ? 
                            <><FaCheck className="status-icon active" /> Active</> : 
                            <><FaTimes className="status-icon blocked" /> Blocked</>
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="card-actions">
                    <button onClick={() => showUpdateForm(teacher)} className="action-button edit">
                      <FaEdit />
                      Edit
                    </button>
                    <button onClick={() => handleDeleteTeacher(teacher.id)} className="action-button delete">
                      <FaTrash />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default TeacherManagement;
